import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { Factory, HelpCircle, Wrench } from "lucide-react";

export const ExpenditureSection: React.FC<{ data: any }> = ({ data }) => {
  const currency = useCurrency();
  return (
    <>
      {/* CapEx & OpEx Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex item-center gap-2">
              <div>
                <CardTitle className="flex items-center page-title gap-2 mb-4">
                  <Factory className="w-5 h-5" />
                  Capital Expenditure (CapEx)
                </CardTitle>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      The total accounting depreciation expense for all assets
                      for the current fiscal year." On hover over a category:
                      "Total annual depreciation for all assets in the [Asset
                      Class].
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-600">
                    {currency}{" "}
                    {data?.result?.capitalExpenditure?.currentPeriod?.value ??
                      "NA"}{" "}
                    {data?.result?.capitalExpenditure?.currentPeriod?.unit ??
                      "NA"}
                  </div>
                  <p className="text-xs text-muted-foreground">Current Month</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">
                    {currency}{" "}
                    {data?.result?.capitalExpenditure?.yearToDate?.value ??
                      "NA"}{" "}
                    {data?.result?.capitalExpenditure?.yearToDate?.unit ?? "NA"}
                  </div>
                  <p className="text-xs text-muted-foreground">Year to Date</p>
                </div>
              </div>
              <div className="space-y-2">
                {data?.result?.capitalExpenditure?.breakdown
                  ?.slice(0, 3)
                  .map((category, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <span className="text-sm">{category.category}</span>
                      <span className="text-sm font-medium">
                        {currency} {category.value}K
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex item-center gap-2">
              <div>
                <CardTitle className="flex items-center page-title gap-2 mb-4">
                  <Wrench className="w-5 h-5" />
                  Operating Expenditure (OpEx)
                </CardTitle>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Day-to-day expenses incurred to run the assets and
                      business.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-xl font-bold text-purple-600">
                    {currency}{" "}
                    {data?.result?.operatingExpenditure?.currentPeriod?.value ??
                      "NA"}{" "}
                    {data?.result?.operatingExpenditure?.currentPeriod?.unit ??
                      "NA"}
                  </div>
                  <p className="text-xs text-muted-foreground">Current Month</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-xl font-bold text-orange-600">
                    {currency}{" "}
                    {data?.result?.operatingExpenditure?.yearToDate?.value ??
                      "NA"}{" "}
                    {data?.result?.operatingExpenditure?.yearToDate?.unit ??
                      "NA"}
                  </div>
                  <p className="text-xs text-muted-foreground">Year to Date</p>
                </div>
              </div>
              <div className="space-y-2">
                {data?.result?.operatingExpenditure?.breakdown.map(
                  (category, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <span className="text-sm">
                        {category.category ?? "NA"}
                      </span>
                      <span className="text-sm font-medium">
                        {currency} {category.value}K
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Efficiency Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="page-title">Cost Efficiency Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {data?.result?.costEfficiencyMetrics?.preventiveVsReactive
                  ?.value ?? "NA"}{" "}
                {data?.result?.costEfficiencyMetrics?.preventiveVsReactive
                  ?.unit ?? "NA"}
              </div>
              <div className="flex item-center justify-center gap-2">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Preventive vs Reactive
                  </p>
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        The percentage of maintenance work that is proactive
                        (planned). A higher percentage indicates better
                        operational control and lower costs.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {currency}{" "}
                {data?.result?.costEfficiencyMetrics?.costPerUnitTreated
                  ?.value ?? "NA"}{" "}
              </div>
              <div className="flex item-center justify-center gap-2">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Cost per Unit Treated
                  </p>
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        The average operational cost to produce a single unit of
                        service (e.g., treat one megaliter of water). A key
                        benchmark for production efficiency
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {data?.result?.costEfficiencyMetrics?.energyEfficiency?.value ??
                  "NA"}
                {data?.result?.costEfficiencyMetrics?.energyEfficiency?.unit}
              </div>
              <div className="flex item-center justify-center gap-2">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Energy Efficiency
                  </p>
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Measures how close actual energy consumption is to the
                        ideal or benchmarked target. A higher percentage is
                        better.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
